import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OutcomeChip, StateChip } from '../../components/atoms.js';
import { SeverityBadge } from '../../components/board.js';
import { ImageLightbox } from '../../components/image-lightbox.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ApiError } from '../../lib/api.js';
import { defectsApi } from '../../lib/defects-api.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { myDefectsApi } from '../../lib/runs-api.js';
import { toast } from '../../lib/toast.js';

// #194: Der Inhalt der Nur-Lesen-Ansicht eines Defects, ohne Rahmen. Zwei Aufrufer: `MyDefectPanel`
// (Seitenleiste, #192) und die rechte Fläche von „Meine Tests“ bei „Fehler prüfen“. Mit
// `onOpenTestCase` wird die Testfall-Zeile zum Knopf (die rechte Fläche öffnet dann den Testfall in
// einer Seitenleiste); ohne bleibt sie reiner Text — `MyDefectPanel` stapelt bewusst keine zweite
// Seitenleiste. „Meine Meldung“ hängt an `is_own` (nur der Melder hat einen Lauf zum Defect), der
// Hinweistext und das Kommentarfeld an `can_add_note` (auch die Prüfer-Vertretung darf schreiben).
// `mode` (#194 Final-Review): im Prüf-Detail (`fixCheck`) stehen „Behoben"/„Nicht behoben" darüber —
// dort wäre „Nur lesen — Änderungen nimmt die Testleitung vor" ein Widerspruch zur Prüfaktion.
export function MyDefectDetails({
  projectId,
  defectId,
  onOpenTestCase,
  mode = 'panel',
}: {
  projectId: string;
  defectId: string;
  /** `trigger` ist der Knopf, der die Seitenleiste öffnet — der Aufrufer gibt ihm beim Schließen den Fokus zurück. */
  onOpenTestCase?: (testCaseId: string, trigger: HTMLElement) => void;
  mode?: 'panel' | 'fixCheck';
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [zoom, setZoom] = useState<number | null>(null);
  // Stabil halten: der Effekt in ImageLightbox hängt an onClose und holt dort den Fokus (#37).
  const closeZoom = useCallback(() => setZoom(null), []);

  const {
    data: defect,
    isError: defectError,
    refetch: refetchDefect,
  } = useQuery({
    queryKey: ['my-defect', projectId, defectId],
    queryFn: () => myDefectsApi.get(projectId, defectId),
    enabled: Boolean(projectId && defectId),
  });
  const { data: config } = useQuery({
    queryKey: ['defect-config', projectId],
    queryFn: () => defectsApi.config(projectId),
    enabled: Boolean(projectId),
  });
  // Den Verlauf darf lesen, wer den Defect sehen darf — auch ohne Notizrecht (Retest-Chip): er soll
  // wissen, was behoben wurde, bevor er nachtestet.
  const {
    data: history,
    isError: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['my-defect-comments', projectId, defectId],
    queryFn: () => myDefectsApi.comments(projectId, defectId),
    enabled: Boolean(projectId && defectId),
  });

  const addNote = useMutation({
    mutationFn: (body: string) => myDefectsApi.addNote(projectId, defectId, body),
    // #194 Final-Review: Fehler meldet die Mutation selbst. Ist die Prüfung inzwischen geschlossen
    // (anderswo bestätigt, Vertretung abgelaufen), endet das Kommentarrecht der Vertretung, und der
    // Server antwortet 404 — ein veralteter Stand, kein Fehler: Hinweis wie bei der Bestätigung
    // (use-fix-checks.ts) und Karten samt Defect neu laden; der Text im Feld bleibt stehen.
    meta: { errorToast: false },
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({
        queryKey: ['my-defect-comments', projectId, defectId],
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404) {
        toast.info(t('fixCheck.stale'));
        void queryClient.invalidateQueries({ queryKey: ['fix-checks', projectId] });
        void queryClient.invalidateQueries({ queryKey: ['my-defect', projectId, defectId] });
        return;
      }
      toast.error(mapApiError(t, err));
    },
  });

  const severity = defect?.severity_id
    ? ((config?.severity_levels ?? []).find((s) => s.severity_id === defect.severity_id) ?? null)
    : null;
  // Der Wortlaut des Testers aus dem Lauf nur, wenn er von der (ggf. überarbeiteten) Beschreibung
  // abweicht — sonst stünde derselbe Text zweimal da. Und nur am EIGENEN Defect: beim Zugriff über
  // den Retest-Chip oder als Prüfer-Vertretung (`is_own` false) gehört der Lauf einem anderen Tester,
  // „Meine Fehlerbeschreibung“ wäre dort falsch beschriftet (#194).
  const showMyReport =
    defect?.is_own === true &&
    Boolean(defect.my_description?.trim()) &&
    defect.my_description?.trim() !== (defect.description ?? '').trim();
  // #194 (B5b): voller Titel, nie gekürzt (TestCaseTitleLine kürzt auf 80 Zeichen / 2 Zeilen).
  const testCaseTitle = defect
    ? pickLang(lang, defect.test_case_title_de, defect.test_case_title_en).trim()
    : '';
  const failedStepText = defect?.failed_step
    ? pickLang(lang, defect.failed_step.description_de, defect.failed_step.description_en)
    : '';
  const failedStepInput = defect?.failed_step
    ? pickLang(lang, defect.failed_step.input_de, defect.failed_step.input_en).trim()
    : '';

  return (
    <>
      {!defect && defectError && <QueryErrorNotice onRetry={() => void refetchDefect()} />}
      {/* #194 Final-Review: im Prüf-Detail stünde die Fläche sonst bis zur Antwort leer da. Die
          Seitenleiste (MyDefectPanel) zeigt „Lädt …“ schon im Titel — dort nicht doppelt. */}
      {mode === 'fixCheck' && !defect && !defectError && (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      )}
      {defect && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SeverityBadge severity={severity} />
            <StateChip label={t(`defect.status.${defect.status}`)} />
            <OutcomeChip stopsChain={defect.stops_chain} />
          </div>

          {/* #194 (15.10., B5b): Die Fehlerbeschreibung ist das Wesentliche — wer nachtestet, muss
              daraus wissen, WAS nachzutesten ist. Deshalb zuerst, groß (text-base), in der
              Haupttextfarbe auf Flächenfarbe mit sichtbarem Rahmen statt der blassen Tönung. */}
          {defect.description?.trim() && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                {t('myDefects.description')}
              </p>
              <p
                data-defect-description
                className="whitespace-pre-wrap break-words rounded-card border border-[var(--border)] bg-bg-surface px-3 py-2.5 text-base leading-relaxed text-text-primary"
              >
                {defect.description}
              </p>
            </div>
          )}

          {showMyReport && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                {t('exec.myReport')}
              </p>
              <p className="whitespace-pre-wrap break-words rounded-card border border-[var(--border)] bg-bg-surface px-3 py-2 text-sm leading-relaxed text-text-primary">
                {defect.my_description}
              </p>
            </div>
          )}

          {/* #194: mit onOpenTestCase ist die ganze Zeile (ID + Titel) ein Knopf. B5b: normale
              Textfarbe und Größe, der Titel bricht um statt abgeschnitten zu werden (trotz „nur lesen"). */}
          {onOpenTestCase && defect.test_case_id ? (
            <button
              type="button"
              onClick={(e) => onOpenTestCase(defect.test_case_id as string, e.currentTarget)}
              aria-label={t('myDefects.openTestCase', {
                id: defect.test_case_human_id ?? defect.title,
              })}
              className="block w-full space-y-0.5 rounded-btn border border-[var(--border)] px-2 py-1.5 text-left hover:bg-bg-subtle"
            >
              {defect.test_case_human_id && (
                <span className="block font-mono text-sm text-text-body">
                  {defect.test_case_human_id}
                </span>
              )}
              {testCaseTitle && (
                <span data-testcase-title className="block break-words text-sm text-text-primary">
                  {testCaseTitle}
                </span>
              )}
            </button>
          ) : (
            <div className="space-y-0.5">
              {defect.test_case_human_id && (
                <p className="font-mono text-sm text-text-body">{defect.test_case_human_id}</p>
              )}
              {testCaseTitle && (
                <p data-testcase-title className="break-words text-sm text-text-primary">
                  {testCaseTitle}
                </p>
              )}
            </div>
          )}

          {defect.failed_step && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                {t('exec.failedStep')}
              </p>
              <p className="break-words text-sm text-text-primary">
                {defect.failed_step.step_no}
                {failedStepText ? `. ${failedStepText}` : ''}
              </p>
              {/* #208: Input des Schritts (bei BW-Tests der Datenquellen-Name). */}
              {failedStepInput && (
                <p className="break-words text-sm text-text-primary">{failedStepInput}</p>
              )}
            </div>
          )}

          <p className="text-xs text-text-muted">
            {!defect.can_add_note
              ? t('myDefects.readOnly')
              : mode === 'fixCheck'
                ? t('fixCheck.detailHint')
                : t('myDefects.readOnlyHint')}
          </p>

          {defect.screenshots.length > 0 && (
            <ul className="flex flex-wrap gap-3">
              {defect.screenshots.map((src, i) => (
                <li key={`shot-${i}-${src.length}`}>
                  <button type="button" onClick={() => setZoom(i)} className="block">
                    <img
                      src={src}
                      alt={`${t('defect.screenshot')} ${i + 1}`}
                      className="max-h-48 rounded-card border border-[var(--border)]"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {zoom !== null && (
            <ImageLightbox
              images={defect.screenshots}
              startIndex={zoom}
              label={t('defect.screenshot')}
              onClose={closeZoom}
            />
          )}

          <div className="space-y-2">
            {historyError ? (
              <QueryErrorNotice compact onRetry={() => void refetchHistory()} />
            ) : history && history.length > 0 ? (
              <ul className="space-y-1">
                {history.map((h) => (
                  <li key={h.comment_id} className="text-xs text-text-body">
                    {h.kind === 'title_ai' ? (
                      // #204: Titelvorschlag der KI als Systemzeile (kein Autor), wie im Defect-Verlauf.
                      <span className="text-text-muted">
                        ✦ {t('defect.thread.titleAi', { old: h.body ?? '' })} ·{' '}
                        {new Date(h.created_at).toLocaleString('de-DE')}
                      </span>
                    ) : h.kind === 'status_change' ? (
                      <span className="text-text-muted">
                        {h.from_status ? t(`defect.status.${h.from_status}`) : '—'} →{' '}
                        {h.to_status ? t(`defect.status.${h.to_status}`) : '—'}
                      </span>
                    ) : (
                      <span className="whitespace-pre-wrap">{h.body}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : history ? (
              <p className="text-xs text-text-muted">{t('defect.thread.empty')}</p>
            ) : null}
            {defect.can_add_note && (
              <>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  aria-label={t('exec.addNote')}
                  placeholder={t('exec.addNotePlaceholder')}
                  className="min-h-[56px] w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
                />
                <button
                  type="button"
                  onClick={() => {
                    const body = note.trim();
                    if (body) addNote.mutate(body);
                  }}
                  disabled={addNote.isPending || note.trim().length === 0}
                  className="rounded-btn bg-brand-accent px-3 py-1 text-xs text-white disabled:opacity-60"
                >
                  {t('exec.saveNote')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
